// Workflow-Verwaltung pro Kanzlei — sehen (Klartext), importieren, freischalten.

import { useCallback, useEffect, useState, type FormEvent } from "react";

import { Button } from "@/components/Button";
import { Loader } from "@/components/Loader";

import {
  activateWorkflow,
  deactivateWorkflow,
  getFirmWorkflows,
  importWorkflow,
  OperatorApiError,
  type OpWorkflow,
} from "./api";

function ImportWorkflowForm({
  firmId,
  onDone,
  onCancel,
}: {
  firmId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [json, setJson] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch {
      setError("Kein gültiges JSON.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await importWorkflow(firmId, parsed);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="op-form" onSubmit={submit}>
      <h3 className="op-h3">Workflow importieren (als Entwurf)</h3>
      <p className="op-secret-hint">
        JSON von Claude einfügen. Wird geprüft und als <strong>inaktiver
        Entwurf</strong> gespeichert — freischalten erst nach deiner Kontrolle.
      </p>
      <textarea
        className="op-input op-textarea"
        value={json}
        onChange={(e) => setJson(e.target.value)}
        rows={8}
        placeholder='{ "schema_version": 1, "workflow_id": "...", "trigger": "...", "steps": [ … ] }'
      />
      {error && <p className="op-error">{error}</p>}
      <div className="op-form-actions">
        <Button
          variant="primary"
          type="submit"
          loading={busy}
          disabled={!json.trim()}
        >
          Importieren
        </Button>
        <button type="button" className="op-btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

export function WorkflowsSection({
  firmId,
  onExpired,
}: {
  firmId: string;
  onExpired: () => void;
}) {
  const [wfs, setWfs] = useState<OpWorkflow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(() => {
    setError(null);
    getFirmWorkflows(firmId)
      .then(setWfs)
      .catch((e: unknown) => {
        if (e instanceof OperatorApiError && e.status === 401) onExpired();
        else setError(e instanceof Error ? e.message : "Fehler beim Laden");
      });
  }, [firmId, onExpired]);

  useEffect(() => {
    load();
  }, [load]);

  async function toggle(wf: OpWorkflow) {
    try {
      if (wf.active_version != null) {
        await deactivateWorkflow(firmId, wf.workflow_id);
      } else {
        await activateWorkflow(firmId, wf.workflow_id, wf.latest_version);
      }
      load();
    } catch (e: unknown) {
      if (e instanceof OperatorApiError && e.status === 401) onExpired();
      else setError(e instanceof Error ? e.message : "Fehler");
    }
  }

  return (
    <>
      <div className="op-toolbar">
        <h2 className="op-h2">Workflows</h2>
        {!showImport && (
          <button
            type="button"
            className="op-btn"
            onClick={() => setShowImport(true)}
          >
            + Workflow importieren
          </button>
        )}
      </div>

      {showImport && (
        <ImportWorkflowForm
          firmId={firmId}
          onDone={() => {
            setShowImport(false);
            load();
          }}
          onCancel={() => setShowImport(false)}
        />
      )}

      {error && <p className="op-error">{error}</p>}

      {!wfs ? (
        <Loader label="Workflows werden geladen…" />
      ) : wfs.length === 0 ? (
        <p className="op-empty">
          Noch keine Workflows. Frag Claude nach einem Flow und importiere die JSON.
        </p>
      ) : (
        wfs.map((wf) => (
          <div key={wf.workflow_id} className="op-wf">
            <div className="op-wf-head">
              <div>
                <strong>{wf.workflow_id}</strong>{" "}
                <span className="op-slug">Auslöser: {wf.trigger}</span>
              </div>
              <div className="op-wf-actions">
                {wf.active_version != null ? (
                  <span className="op-wf-on">aktiv · v{wf.active_version}</span>
                ) : (
                  <span className="op-wf-off">Entwurf · v{wf.latest_version}</span>
                )}
                <button
                  type="button"
                  className="op-btn-ghost"
                  onClick={() => toggle(wf)}
                >
                  {wf.active_version != null
                    ? "Deaktivieren"
                    : `Freischalten (v${wf.latest_version})`}
                </button>
              </div>
            </div>
            <ol className="op-wf-steps">
              {wf.steps_plain.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </div>
        ))
      )}
    </>
  );
}
